import type { UpdateReportSchema } from '@/entities/report'
import type { SavedFilterModel } from '@/shared/generated'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, Trash2Icon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	updateReportSchema,
	useDeleteSavedFilterMutation,
	useUpdateSavedFilterMutation,
} from '@/entities/report'
import { Access } from '@/entities/session'

import {
	FormProvider,
	RHFCheckbox,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	FieldDescription,
	FieldGroup,
	FieldLegend,
	FieldSet,
} from '@/shared/components/ui/field'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { SavedFilterType } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import {
	actDeleted,
	actUpdated,
	errorMessage,
	processingMessage,
	successActionMessage,
} from '@/shared/tools/message'

import { QueryFiltersField } from './QueryFiltersField'

type UpdateReportModal = {
	report: SavedFilterModel & { is_global?: boolean }
	isOpen: boolean
	close(): void
}

export function EditReportModal({ isOpen, close, report }: UpdateReportModal) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const onSuccess = async () =>
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: ['saved-filters'],
			}),
		])
	const deleteSavedFilter = useDeleteSavedFilterMutation({
		onSuccess,
	})
	const updateSavedFilter = useUpdateSavedFilterMutation(report.id, {
		onSuccess,
	})
	const isPending = updateSavedFilter.isPending || deleteSavedFilter.isPending
	const methods = useForm({
		resolver: zodResolver(updateReportSchema),
		values: {
			name: report.name,
			type: report.type,
			is_public: report.is_public || false,
			filters: report.filters ?? [],
		},
		defaultValues: {
			name: '',
			type: SavedFilterType.Products,
			is_public: false,
			filters: [],
		},
	})
	const handleDelete = async () => {
		if (!report?.id) return
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete'),
			cancelText: t('general_cancel'),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (!answer) return
		toast.promise(deleteSavedFilter.mutateAsync(report.id), {
			loading: processingMessage(t),
			success: successActionMessage('Report', actDeleted(t), t),
			error: errorMessage(t),
		})
		close()
	}

	const _handleSubmit = async (body: UpdateReportSchema) => {
		ampli.reportsUpdateReport({
			'Is global': body.is_public,
		})
		const result = toast.promise(updateSavedFilter.mutateAsync(body), {
			loading: processingMessage(t),
			success: successActionMessage('Report', actUpdated(t), t),
			error: errorMessage(t),
		})
		await result.unwrap()
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='min-w-3xl'>
				<ModalHeader className='sr-only'>
					<ModalTitle>{t('saved_reports.update.modal.title')}</ModalTitle>
					<ModalDescription className='sr-only'>
						{t('saved_reports.update.modal.description', {
							defaultValue: 'Edit the details of your saved report',
						})}
					</ModalDescription>
				</ModalHeader>
				<ModalBody className='flex flex-col'>
					<Blanket
						isTinted={isPending}
						className='absolute flex items-center justify-center bg-white/50 backdrop-blur-xs dark:bg-black/50'
					>
						<Loader />
					</Blanket>
					<FormProvider
						methods={methods}
						onSubmit={handleSubmit}
						className='flex w-full flex-col gap-3'
					>
						<FieldGroup>
							<FieldSet>
								<FieldLegend>
									{t('saved_reports.update.modal.title')}
								</FieldLegend>
								<FieldDescription>
									{t('saved_reports.update.modal.description', {
										defaultValue: 'Edit the details of your saved report',
									})}
								</FieldDescription>
								<FieldGroup>
									<RHFTextField
										label={t('report_name')}
										name='name'
										className='w-full'
									/>
									<Access permissions={['MANAGE_GLOBAL_FILTERS']}>
										<RHFCheckbox
											label={t('make_visible_for_all')}
											name='is_public'
										/>
									</Access>
									<QueryFiltersField
										name='filters'
										label={t('app.filters', { defaultValue: 'Filters' })}
									/>
								</FieldGroup>
							</FieldSet>
						</FieldGroup>
					</FormProvider>
				</ModalBody>
				<ModalFooter>
					<ModalClose asChild>
						<Button>{t('general_cancel')}</Button>
					</ModalClose>
					<div className='ml-auto flex items-center gap-2'>
						<Button onClick={handleDelete}>
							<Trash2Icon />
							{t('general_delete')}
						</Button>
						<Button
							variant='primary'
							disabled={isPending}
							onClick={handleSubmit}
						>
							{isPending ? <Spinner /> : <CheckIcon />}
							{t('general_save')}
						</Button>
					</div>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
